import { useEffect, useState } from 'react';
import {
  DATA_LOCATION_OPTIONS,
  FOCUS_AREA_OPTIONS,
  SENSITIVITY_OPTIONS,
  TEAM_SIZE_OPTIONS,
} from '../constants.js';
import { clearDraft, readDraft, saveDraft } from '../persistence.js';
import type { DataLocation, Depth, DiscoverIntake, FocusArea, Sensitivity } from '../types.js';
import { DepthSelector, Field, Select } from './fields.js';

const DRAFT_NS = 'discover';

interface DiscoverIntakeFormProps {
  onSubmit: (intake: DiscoverIntake, depth: Depth) => void;
}

type Draft = DiscoverIntake & { depth?: Depth };

function intakeFromDraft(draft: Draft | null): Partial<DiscoverIntake> {
  if (!draft) return {};
  const rest: Partial<Draft> = { ...draft };
  delete rest.depth;
  return rest;
}

const EMPTY: DiscoverIntake = {
  name: '',
  email: '',
  company: '',
  website_url: '',
  ai_motivation: '',
  focus_areas: [],
  team_size: '',
  data_location: [],
  data_location_other: '',
  pain_points: '',
  sensitivity: '',
  desired_outcome: '',
};

export function DiscoverIntakeForm({ onSubmit }: DiscoverIntakeFormProps): React.JSX.Element {
  const [form, setForm] = useState<DiscoverIntake>(() => ({
    ...EMPTY,
    ...intakeFromDraft(readDraft<Draft>(DRAFT_NS)),
  }));
  const [depth, setDepth] = useState<Depth>(() => readDraft<Draft>(DRAFT_NS)?.depth ?? 'fast');
  const [touched, setTouched] = useState(false);
  const [showDataOther, setShowDataOther] = useState(() =>
    Boolean((readDraft<Draft>(DRAFT_NS)?.data_location_other ?? '').trim()),
  );

  useEffect(() => {
    saveDraft({ ...form, depth }, DRAFT_NS);
  }, [form, depth]);

  function set<K extends keyof DiscoverIntake>(key: K, value: DiscoverIntake[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggle<T extends string>(key: 'focus_areas' | 'data_location', value: T): void {
    setForm((f) => {
      const list = f[key] as string[];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...f, [key]: next };
    });
  }

  function toggleDataOther(): void {
    const next = !showDataOther;
    setShowDataOther(next);
    if (!next) set('data_location_other', '');
  }

  const missing = validate(form);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setTouched(true);
    if (missing.length === 0) {
      clearDraft(DRAFT_NS);
      onSubmit(form, depth);
    }
  }

  return (
    <form className="tbo-form" onSubmit={handleSubmit} noValidate>
      <p className="tbo-lead">
        You want to put AI to work but aren't sure where to start. Tell us a little about your
        organisation — we'll read your site, ask a few sharp questions, then pinpoint the
        highest-value AI opportunity for you.
      </p>

      <fieldset>
        <legend>About you</legend>
        <Field label="Your name" required>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Work email" required>
          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Company" required>
          <input value={form.company} onChange={(e) => set('company', e.target.value)} />
        </Field>
        <Field label="Website URL" required>
          <input
            type="url"
            placeholder="https://…"
            value={form.website_url}
            onChange={(e) => set('website_url', e.target.value)}
          />
        </Field>
      </fieldset>

      <fieldset>
        <legend>Your organisation</legend>
        <Field label="What's driving your interest in AI right now?" required>
          <textarea
            rows={4}
            placeholder="e.g. board mandate, efficiency pressure, competitors moving…"
            value={form.ai_motivation}
            onChange={(e) => set('ai_motivation', e.target.value)}
          />
        </Field>

        <Field label="Which parts of the business feel most manual or time-consuming?" required>
          <div className="tbo-checks">
            {FOCUS_AREA_OPTIONS.map((opt) => (
              <label key={opt} className="tbo-check">
                <input
                  type="checkbox"
                  checked={form.focus_areas.includes(opt)}
                  onChange={() => toggle<FocusArea>('focus_areas', opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        </Field>

        <Field label="How big is your organisation?" required>
          <Select
            value={form.team_size}
            options={TEAM_SIZE_OPTIONS}
            onChange={(v) => set('team_size', v as DiscoverIntake['team_size'])}
          />
        </Field>

        <Field label="Where does most of your information live today?" required>
          <div className="tbo-checks">
            {DATA_LOCATION_OPTIONS.map((opt) => (
              <label key={opt} className="tbo-check">
                <input
                  type="checkbox"
                  checked={form.data_location.includes(opt)}
                  onChange={() => toggle<DataLocation>('data_location', opt)}
                />
                {opt}
              </label>
            ))}
            <label className="tbo-check">
              <input type="checkbox" checked={showDataOther} onChange={toggleDataOther} />
              Other
            </label>
          </div>
          {showDataOther && (
            <input
              className="tbo-detail"
              placeholder="Please specify where else the information lives"
              value={form.data_location_other ?? ''}
              onChange={(e) => set('data_location_other', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="What are your top 3–4 biggest pain points, ranked by urgency/impact?"
          required
        >
          <textarea
            rows={4}
            placeholder="Most pressing first — one per line. e.g.&#10;1. Manual invoice processing eats finance's week&#10;2. Slow customer response times&#10;3. Compliance reporting done by hand"
            value={form.pain_points}
            onChange={(e) => set('pain_points', e.target.value)}
          />
        </Field>

        <Field label="How sensitive is the data you work with?" required>
          <Select
            value={form.sensitivity}
            options={SENSITIVITY_OPTIONS}
            onChange={(v) => set('sensitivity', v as Sensitivity)}
          />
        </Field>

        <Field label="What would a win look like in the next 6–12 months? (optional)">
          <textarea
            rows={3}
            value={form.desired_outcome ?? ''}
            onChange={(e) => set('desired_outcome', e.target.value)}
          />
        </Field>
      </fieldset>

      <DepthSelector depth={depth} onChange={setDepth} />

      {touched && missing.length > 0 && (
        <div className="tbo-error">Please complete: {missing.join(', ')}.</div>
      )}

      <button className="tbo-btn" type="submit">
        Find my AI opportunity →
      </button>
    </form>
  );
}

function validate(form: DiscoverIntake): string[] {
  const missing: string[] = [];
  if (!form.name.trim()) missing.push('name');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) missing.push('a valid email');
  if (!form.company.trim()) missing.push('company');
  if (!/^https?:\/\/.+/.test(form.website_url)) missing.push('website URL');
  if (!form.ai_motivation.trim()) missing.push('your AI motivation');
  if (form.focus_areas.length === 0) missing.push('at least one focus area');
  if (!form.team_size) missing.push('organisation size');
  if (form.data_location.length === 0 && !(form.data_location_other ?? '').trim())
    missing.push('data location');
  if (!form.pain_points.trim()) missing.push('your top pain points');
  if (!form.sensitivity) missing.push('data sensitivity');
  return missing;
}
